import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Languages, LifeBuoy } from "lucide-react";
import { CALL_NUMBER } from "../data/services";
import { useLanguage } from "../i18n/LanguageContext";

export const Header = () => {
  const { t, lang, toggle } = useLanguage();
  const [helpOpen, setHelpOpen] = useState(false);
  const links = [
    { label: t.nav.home, href: "#home", id: "home" },
    { label: t.nav.events, href: "#events", id: "events" },
    { label: t.nav.cleaning, href: "#cleaning", id: "cleaning" },
    { label: t.nav.garden, href: "#garden", id: "garden" },
  ];
  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 w-full z-50 bg-white/70 backdrop-blur-xl border-b border-white/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 sm:h-20 flex items-center justify-between gap-3">
        <a href="#home" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0">
          <img
            src="/assets/logo.png"
            alt="Alpha & More logo"
            className="h-11 w-11 sm:h-12 sm:w-12 rounded-full object-cover shadow-sm"
          />
          <span className="font-display text-lg sm:text-xl font-semibold tracking-tight text-stone-900 hidden sm:block">
            Alpha <span className="text-[#E85D75]">&amp;</span> More
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-7" data-testid="header-nav">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={`nav-${l.id}-link`}
              className="text-sm font-medium text-stone-600 hover:text-[#E85D75] transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle-button"
            className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white text-stone-700 text-sm font-semibold px-3.5 sm:px-4 py-2 hover:border-[#E85D75] hover:text-[#E85D75] transition-colors duration-200"
          >
            <Languages className="w-4 h-4" />
            {lang === "en" ? "বাংলা" : "EN"}
          </button>
          <div className="relative">
            <button
              onClick={() => setHelpOpen((o) => !o)}
              data-testid="header-help-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#E85D75] text-white text-sm font-semibold px-4 sm:px-5 py-2.5 shadow-[0_8px_20px_rgba(232,93,117,0.35)] hover:bg-[#d64a63] hover:-translate-y-0.5 transition-all duration-200"
            >
              <LifeBuoy className="w-4 h-4" />
              {t.help}
            </button>
            <AnimatePresence>
              {helpOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setHelpOpen(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.97 }}
                    transition={{ duration: 0.18 }}
                    data-testid="header-help-popover"
                    className="absolute right-0 top-full mt-3 z-50 w-64 rounded-3xl bg-white border border-stone-200 shadow-[0_20px_50px_rgb(0,0,0,0.12)] p-5"
                  >
                    <p className="text-xs font-semibold tracking-widest text-stone-400 uppercase">
                      {t.help}
                    </p>
                    <p className="mt-1.5 text-sm text-stone-500">{t.helpLine}</p>
                    <a
                      href={`tel:${CALL_NUMBER}`}
                      data-testid="header-help-call-link"
                      className="mt-3 flex items-center justify-center gap-2 rounded-full bg-[#E85D75] text-white font-semibold px-5 py-3 hover:bg-[#d64a63] transition-colors duration-200"
                    >
                      <Phone className="w-4 h-4" />
                      {CALL_NUMBER}
                    </a>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </motion.header>
  );
};
